<!--
  AModal — Pineapple 톤 공용 모달
  ────────────────────────────────────────
  props:
    open: v-model 호환 — 모달 열림 여부 (Boolean)
    title: 헤더 제목
    sub: 헤더 보조 문구 (선택)
    size: sm/md/lg/xl (max-width 가변)
    closeOnBackdrop: 백드롭 클릭 시 닫힘 여부 (기본 true)
    persistent: ESC/백드롭 무시 (강제 응답 필요한 모달)
  슬롯:
    default: 본문
    head: 헤더 우측 영역 (제목 옆 보조 아이콘/뱃지)
    foot: 푸터 (취소/확인 버튼 등) — 비어있으면 푸터 영역 미렌더
-->
<template>
  <transition name="a-modal">
    <div
      v-if="open"
      class="a-modal-backdrop"
      @click.self="onBackdropClick"
      @keydown.esc="onEsc"
      tabindex="-1"
    >
      <div class="a-modal" :class="`is-${size}`" role="dialog" aria-modal="true">
        <div class="a-modal__head">
          <div class="a-modal__title-block">
            <div class="a-modal__title">{{ title }}</div>
            <div v-if="sub" class="a-modal__sub">{{ sub }}</div>
          </div>
          <div class="a-modal__head-right">
            <slot name="head" />
            <button class="a-modal__close" type="button" aria-label="닫기" @click="close">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>
        <div class="a-modal__body">
          <slot />
        </div>
        <div v-if="$slots.foot" class="a-modal__foot">
          <slot name="foot" />
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'AModal',
  props: {
    open: { type: Boolean, default: false },
    title: { type: String, default: '' },
    sub: { type: String, default: '' },
    size: {
      type: String,
      default: 'md',
      validator: (v) => ['sm', 'md', 'lg', 'xl', 'full'].includes(v),
    },
    closeOnBackdrop: { type: Boolean, default: true },
    persistent: { type: Boolean, default: false },
  },
  emits: ['update:open', 'close'],
  watch: {
    open(v) {
      // body 스크롤 잠금
      if (v) document.body.style.overflow = 'hidden';
      else document.body.style.overflow = '';
    },
  },
  beforeUnmount() {
    document.body.style.overflow = '';
  },
  methods: {
    close() {
      this.$emit('update:open', false);
      this.$emit('close');
    },
    onBackdropClick() {
      if (this.persistent || !this.closeOnBackdrop) return;
      this.close();
    },
    onEsc() {
      if (this.persistent) return;
      this.close();
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/scss/_pineapple.scss';

.a-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(31, 27, 20, 0.5);
  display: grid;
  place-items: center;
  /* slide-panel-overlay (10000), 학습활동 드로어 등 그 어떤 패널보다 위에 표시.
     모달이 항상 가장 앞으로 와야 사용자가 패널을 닫지 않고도 연속해서 모달 작업 가능 */
  z-index: 11000;
  padding: 24px;
}

.a-modal {
  background: $p-panel;
  border-radius: 16px;
  width: 100%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  font-family: $p-font-body;
  color: $p-ink;
  box-shadow: 0 24px 48px -16px rgba(0, 0, 0, 0.32);
  overflow: hidden;

  &.is-sm { max-width: 420px; }
  &.is-md { max-width: 560px; }
  &.is-lg { max-width: 800px; }
  &.is-xl { max-width: 1080px; }
  &.is-full {
    max-width: 1440px;
    width: 95vw;
    height: 92vh;
  }

  &__head {
    padding: 18px 22px;
    border-bottom: 1px solid $p-line;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    flex-shrink: 0;
  }
  &__title-block {
    min-width: 0;
    flex: 1;
  }
  &__title {
    font-size: 15px;
    font-weight: 800;
    letter-spacing: -0.01em;
    color: $p-ink;
    line-height: 1.3;
  }
  &__sub {
    font-size: 11px;
    color: $p-ink-soft;
    margin-top: 4px;
  }
  &__head-right {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
  }
  &__close {
    width: 28px;
    height: 28px;
    border-radius: 6px;
    border: 0;
    background: transparent;
    color: $p-ink-mid;
    cursor: pointer;
    display: inline-grid;
    place-items: center;
    transition: background 0.15s ease, color 0.15s ease;

    &:hover { background: $p-bg; color: $p-ink; }
  }

  &__body {
    flex: 1;
    overflow: auto;
    padding: 22px;
    min-height: 0;
  }

  &__foot {
    padding: 14px 22px;
    border-top: 1px solid $p-line;
    background: $p-panel-alt;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
  }
}

/* transition */
.a-modal-enter-active, .a-modal-leave-active {
  transition: opacity 0.2s ease;
}
.a-modal-enter-active .a-modal,
.a-modal-leave-active .a-modal {
  transition: transform 0.2s ease, opacity 0.2s ease;
}
.a-modal-enter-from, .a-modal-leave-to { opacity: 0; }
.a-modal-enter-from .a-modal,
.a-modal-leave-to .a-modal { transform: scale(0.96); opacity: 0; }
</style>
